/**
 * ContactSupportModal — global support-ticket entry point.
 *
 * Rendered by both the dashboard footer (authenticated users) and the
 * landing page footer (public visitors). Auth state is lifted to the
 * parent so this component stays context-agnostic — the caller passes
 * `initialEmail` (optional) and `emailLocked` (readonly flag):
 *   - Dashboard footer     → initialEmail={user.email}, emailLocked
 *   - Landing page footer  → initialEmail="", emailLocked=false
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Loader2, LifeBuoy } from 'lucide-react';
import api from '@/lib/api';
import { getAuthToken } from '@/lib/tokenStorage';

const CATEGORIES = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'billing', label: 'Billing' },
  { value: 'other', label: 'Other' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSupportModal = ({
  open,
  onOpenChange,
  initialEmail = '',
  emailLocked = false,
}) => {
  const [category, setCategory] = useState('feedback');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);

  // Reset email whenever the modal is opened OR the parent's identity
  // changes (e.g. user logs in while the modal is mounted).
  useEffect(() => {
    if (open) setEmail(initialEmail);
  }, [open, initialEmail]);

  const reset = () => {
    setCategory('feedback');
    setSubject('');
    setMessage('');
    setEmail(initialEmail);
  };

  const submit = async () => {
    const trimmedSubject = subject.trim();
    const trimmedMessage = message.trim();
    const trimmedEmail = (email || '').trim();

    if (trimmedSubject.length < 3) { toast.error('Subject is too short (min 3 characters).'); return; }
    if (trimmedMessage.length < 10) { toast.error('Please tell us a bit more (min 10 characters).'); return; }
    // Only validate email on the client for anonymous callers (locked emails
    // are already known-good — they come from the authenticated session).
    if (!emailLocked && !/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      toast.error('Please enter a valid email so we can reply.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        category,
        subject: trimmedSubject,
        message: trimmedMessage,
        source_path: typeof window !== 'undefined' ? window.location.pathname : null,
      };
      // Backend derives email from JWT when a valid token is present, so we
      // only include it explicitly for anonymous submissions.
      if (!emailLocked) payload.email = trimmedEmail;

      const token = getAuthToken();
      if (token) {
        // Use the authenticated axios instance (adds JWT + interceptors).
        await api.post('/support/tickets', payload);
      } else {
        // Plain axios call — no auth token, no interceptors needed.
        await axios.post(`${BACKEND_URL}/api/support/tickets`, payload, {
          headers: { 'Content-Type': 'application/json' },
        });
      }
      toast.success('Thanks — your message is on its way to our support team.');
      reset();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Something went wrong. Please try again.';
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-[#0F1730]" data-testid="contact-support-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-100">
            <LifeBuoy className="h-5 w-5 text-rose-400" />
            Contact Support
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Share a bug, feature request, or feedback. We&apos;ll reply within one business day.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="support-category" className="text-slate-200 text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger
                id="support-category"
                className="bg-[#0A1024] border-[#1F2A4D] text-slate-100"
                data-testid="support-category-trigger"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`support-category-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-subject" className="text-slate-200 text-xs">Subject</Label>
            <Input
              id="support-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary"
              maxLength={200}
              className="bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500"
              data-testid="support-subject"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-message" className="text-slate-200 text-xs">Message</Label>
            <Textarea
              id="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's on your mind — include steps to reproduce if this is a bug."
              rows={6}
              maxLength={5000}
              className="bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500 resize-none"
              data-testid="support-message"
            />
            <div className="text-[10px] text-slate-500 text-right">
              {message.length}/5000
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="support-email" className="text-slate-200 text-xs">
              Your email {emailLocked ? '(from your account)' : ''}
            </Label>
            <Input
              id="support-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              readOnly={emailLocked}
              className={`bg-[#0A1024] border-[#1F2A4D] text-slate-100 placeholder:text-slate-500 ${
                emailLocked ? 'opacity-70 cursor-not-allowed' : ''
              }`}
              data-testid="support-email"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="border-[#1F2A4D] text-slate-200 hover:bg-[#0A1024]"
            data-testid="support-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="support-submit"
          >
            {submitting ? (<><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Sending…</>) : 'Send Message'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContactSupportModal;
